import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageMeta from '../../components/PageMeta';
import api from '../../utils/api';
import { getProfileImageUrl } from '../../utils/imageUtils';
import { formatFarmerLocation } from '../../utils/addressUtils';
import { useTranslation } from 'react-i18next';
import {
  FaSearch, FaTimes, FaStar, FaMapMarkerAlt, FaCheckCircle,
  FaBoxOpen, FaArrowRight, FaFilter
} from 'react-icons/fa';
import { ROLE_IMG } from '../../utils/roleAssets';
import Pagination from '../../components/Pagination';
import './Farmers.css';

const PER_PAGE = 10;

const Farmers = () => {
  const { t } = useTranslation();
  const [farmers, setFarmers] = useState([]);
  const [filteredFarmers, setFilteredFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => { fetchFarmers(); }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmers, searchQuery, selectedDistrict, minRating]);

  const fetchFarmers = async () => {
    setLoading(true);
    try {
      let farmersData = [];
      try {
        const res = await api.get('/farmers');
        farmersData = res.data || [];
      } catch (e) {
        const prodRes = await api.get('/products');
        const map = new Map();
        (prodRes.data || []).forEach(p => {
          if (p.farmer?._id) map.set(p.farmer._id.toString(), p.farmer);
        });
        farmersData = Array.from(map.values());
      }

      const prodRes = await api.get('/products');
      const prodData = prodRes.data || [];
      const prodMap = new Map();
      prodData.forEach(p => {
        if (p.farmer?._id) {
          const id = p.farmer._id.toString();
          if (!prodMap.has(id)) prodMap.set(id, []);
          prodMap.get(id).push(p);
        }
      });

      const reviewsMap = new Map();
      for (const f of farmersData) {
        try {
          const r = await api.get(`/reviews/farmer/${f._id}`);
          if (Array.isArray(r.data)) reviewsMap.set(f._id.toString(), r.data);
        } catch (e) { /* ignore */ }
      }

      const arr = farmersData
        .filter(f => f.verified === true && f.role === 'farmer')
        .map(f => {
          const id = f._id.toString();
          const products = prodMap.get(id) || [];
          const reviews = reviewsMap.get(id) || [];
          let rating = 4.5;
          if (reviews.length > 0) rating = reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;
          return {
            _id: f._id,
            username: f.username || id,
            name: f.name || 'Local Farmer',
            district: f.address?.district || '',
            province: f.address?.province || '',
            address: f.address,
            verified: f.verified || false,
            rating: Math.round(rating * 10) / 10,
            totalReviews: reviews.length,
            totalProducts: products.length,
            profileImage: f.profileImage || null,
            bio: f.bio || '',
            specialties: f.specialties || [],
            joinedDate: f.createdAt || new Date()
          };
        })
        .sort((a, b) => b.rating !== a.rating ? b.rating - a.rating : b.totalProducts - a.totalProducts);

      setFarmers(arr);
    } catch (e) {
      console.error('Error fetching farmers:', e);
      setFarmers([]);
    } finally { setLoading(false); }
  };

  const applyFilters = () => {
    let filtered = [...farmers];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(f =>
        f.name.toLowerCase().includes(q) ||
        f.district.toLowerCase().includes(q) ||
        f.bio.toLowerCase().includes(q) ||
        f.specialties.some(s => s.toLowerCase().includes(q))
      );
    }
    if (selectedDistrict) filtered = filtered.filter(f => f.district === selectedDistrict);
    if (minRating > 0) filtered = filtered.filter(f => f.rating >= minRating);
    filtered.sort((a, b) => b.rating - a.rating);
    setFilteredFarmers(filtered);
  };

  useEffect(() => { setPage(1); }, [filteredFarmers.length]);

  const totalPages = Math.ceil(filteredFarmers.length / PER_PAGE);
  const pageFarmers = filteredFarmers.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const hasFilters = searchQuery || selectedDistrict || minRating > 0;

  const renderStars = (rating) => {
    const full = Math.floor(rating);
    return (
      <div className="fm-stars">
        {[...Array(5)].map((_, i) => (
          <FaStar key={i} className={i < full ? 'fm-star--filled' : 'fm-star--empty'} />
        ))}
      </div>
    );
  };

  return (
    <div className="fm-page">
      <PageMeta
        title={t('farmers.pageTitle')}
        description={t('farmers.subtitle')}
        path="/farmers"
      />
      <Header />

      {/* Hero */}
      <section className="fm-hero">
        <div className="fm-hero-inner">
          <div className="fm-hero-icon">
            <img src={ROLE_IMG.farmer()} alt="" className="fm-hero-role-img" />
          </div>
          <h1>{t('farmers.title')}</h1>
          <p>{t('farmers.subtitle')}</p>
        </div>
      </section>

      {/* Filters */}
      <section className="fm-filters">
        <div className="fm-filters-inner">
          <div className="fm-search-field">
            <FaSearch className="fm-search-icon" />
            <input
              type="text"
              placeholder={t('farmers.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="fm-search-clear" onClick={() => setSearchQuery('')}><FaTimes /></button>
            )}
          </div>
          <div className="fm-filter-row">
            <div className="fm-select-wrap">
              <FaFilter className="fm-select-icon" />
              <select value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)}>
                <option value="">{t('farmers.allDistricts')}</option>
                <option value="Morang">Morang</option>
                <option value="Sunsari">Sunsari</option>
                <option value="Jhapa">Jhapa</option>
              </select>
            </div>
            <div className="fm-select-wrap">
              <FaStar className="fm-select-icon" />
              <select value={minRating} onChange={e => setMinRating(parseFloat(e.target.value))}>
                <option value="0">{t('farmers.allRatings')}</option>
                <option value="4.5">4.5+ Stars</option>
                <option value="4.0">4.0+ Stars</option>
              </select>
            </div>
            {hasFilters && (
              <button className="fm-reset" onClick={() => { setSearchQuery(''); setSelectedDistrict(''); setMinRating(0); }}>
                <FaTimes /> {t('farmers.resetFilters')}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="fm-content">
        <div className="fm-content-inner">
          {loading ? (
            <div className="fm-loading">
              <div className="fm-dots"><span /><span /><span /></div>
              <p>{t('farmers.loading')}</p>
            </div>
          ) : filteredFarmers.length === 0 ? (
            <div className="fm-empty">
              <img src={ROLE_IMG.farmer()} alt="" className="fm-empty-icon fm-empty-icon--img" />
              <h3>{t('farmers.noFarmers')}</h3>
              <p>{t('farmers.tryAdjusting')}</p>
            </div>
          ) : (
            <>
              <div className="fm-count">{t('farmers.foundFarmers', { count: filteredFarmers.length })}</div>
              <div className="fm-grid">
                {pageFarmers.map(farmer => (
                  <div key={farmer._id} className="fm-card">
                    {/* Top */}
                    <div className="fm-card-top">
                      <div className="fm-avatar-wrap">
                        <img
                          src={farmer.profileImage ? getProfileImageUrl(farmer.profileImage) : '/images/deafultpic.png'}
                          alt={farmer.name}
                          referrerPolicy="no-referrer"
                          onError={e => { e.target.onerror = null; e.target.src = '/images/deafultpic.png'; }}
                        />
                        {farmer.verified && (
                          <span className="fm-verified"><FaCheckCircle /></span>
                        )}
                      </div>
                      <div className="fm-card-info">
                        <h3>{farmer.name}</h3>
                        <span className="fm-loc"><FaMapMarkerAlt /> {formatFarmerLocation(farmer)}</span>
                        <div className="fm-rating-row">
                          {renderStars(farmer.rating)}
                          <span className="fm-rating-num">{farmer.rating}</span>
                          <span className="fm-rating-count">{t('farmers.reviews', { count: farmer.totalReviews })}</span>
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="fm-card-body">
                      {farmer.bio && <p className="fm-bio">{farmer.bio}</p>}
                      {farmer.specialties.length > 0 && (
                        <div className="fm-tags">
                          {farmer.specialties.map((s, i) => (
                            <span key={i} className="fm-tag">{s}</span>
                          ))}
                        </div>
                      )}
                      <div className="fm-stats-row">
                        <div className="fm-stat">
                          <FaBoxOpen />
                          <span><strong>{farmer.totalProducts}</strong> {t('farmers.productsLabel')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="fm-card-foot">
                      <Link to={`/users/${farmer.username || farmer._id}`} className="fm-btn fm-btn--primary">
                        {t('farmers.viewProfile')} <FaArrowRight />
                      </Link>
                      <Link to={`/products?farmer=${farmer._id}`} className="fm-btn fm-btn--outline">
                        {t('farmers.viewProducts')}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Farmers;
